import React, { useCallback, useRef } from 'react';

import { Controller, useForm } from 'react-hook-form';
import dayjs from '~/timeInit';

import { History as HistoryIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Divider,
  GridLegacy as Grid,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

import { COUNTRIES } from '~/consts/priceParrot';

import { Property } from '~/types/services/priceParrot';

import getAllCurrencyCodes from '~/utils/getAllCurrencyCodes';

import LocationAutocomplete from '../LocationAutocomplete';

import EmailGenerator from './EmailGenerator';
import ParityReport from './ParityReport';
import RecentScrapes from './RecentScrapes';
import { exportToPdf } from './exportPdf';
import { MAX_DAYS_AHEAD, useParityToolOrchestration } from './useParityToolOrchestration';

type FormInput = {
  locationUrl: string;
  daysAhead: number;
  currencyCode: string;
  countryCode: string;
};

export default function PriceParrotParityTool() {
  const { control, handleSubmit, getValues, setValue } = useForm<FormInput>({
    defaultValues: {
      locationUrl: '',
      daysAhead: MAX_DAYS_AHEAD,
      currencyCode: 'AUD',
      countryCode: 'AU',
    },
  });

  const {
    progress,
    progressText,
    error,
    clearError,
    selectedNights,
    setSelectedNights,
    currentAnalysis,
    availableNights,
    isHealthy,
    isWorking,
    revenueHooks,
    offerId,
    ratePlans,
    ratePlansLoading,
    ratePlansError,
    lpcPromotionalDiscounts,
    promotionsLoading,
    promotionsError,
    cheapestPlan,
    emailExpanded,
    setEmailExpanded,
    submitFreshScrape,
    loadLatestReport,
    loadFromJob,
  } = useParityToolOrchestration();

  const selectedProperty = useRef<Property | null>(null);
  const reportRef = useRef<HTMLDivElement>(null);
  const emailRef = useRef<HTMLDivElement>(null);

  const onFreshScrape = async (data: FormInput) => {
    await submitFreshScrape({
      ...data,
      propertyName: selectedProperty.current?.name || data.locationUrl,
    });
  };

  const onUseLatest = useCallback(async () => {
    const locationUrl = getValues('locationUrl');
    const currencyCode = getValues('currencyCode');
    const countryCode = getValues('countryCode');
    if (!locationUrl) {
      return;
    }

    try {
      const propName = selectedProperty.current?.name || locationUrl;
      await loadLatestReport(locationUrl, currencyCode, countryCode, propName);
    } catch {
      // Errors are handled inside the hook
    }
  }, [getValues, loadLatestReport]);

  const onSelectRecentScrape = useCallback(
    async (job: Parameters<typeof loadFromJob>[0]) => {
      setValue('locationUrl', job.locationUrl);
      setValue('daysAhead', dayjs(job.endDate).diff(dayjs(job.startDate), 'day'));
      setValue('currencyCode', job.currencyCode);
      setValue('countryCode', job.countryCode);
      await loadFromJob(job);
    },
    [loadFromJob, setValue],
  );

  const handleGenerateEmail = useCallback(() => {
    setEmailExpanded(true);
    setTimeout(() => {
      emailRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, [setEmailExpanded]);

  const handleExportPdf = useCallback(() => {
    if (reportRef.current && currentAnalysis) {
      exportToPdf(reportRef.current, currentAnalysis.propertyName, currentAnalysis.analysisDate);
    }
  }, [currentAnalysis]);

  return (
    <Box>
      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
        Generate a parity report
      </Typography>
      <form onSubmit={handleSubmit(onFreshScrape)}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={4}>
            <LocationAutocomplete
              control={control}
              name="locationUrl"
              label="Property"
              rules={{ required: 'Required' }}
              onPropertySelect={(p) => {
                selectedProperty.current = p;
              }}
            />
          </Grid>
          <Grid item xs={1.5}>
            <Controller
              name="daysAhead"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Days Ahead"
                  type="number"
                  size="small"
                  fullWidth
                  InputProps={{ inputProps: { min: 7, max: MAX_DAYS_AHEAD } }}
                />
              )}
            />
          </Grid>
          <Grid item xs={1.5}>
            <Controller
              name="currencyCode"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Currency" size="small" fullWidth select>
                  {getAllCurrencyCodes().map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid item xs={1.5}>
            <Controller
              name="countryCode"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Country" size="small" fullWidth select>
                  {COUNTRIES.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.code}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid item xs={3.5}>
            <Box sx={{ display: 'flex', gap: 1.5, height: 40 }}>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={onUseLatest}
                disabled={isWorking}
                sx={{ flex: 1, whiteSpace: 'nowrap' }}
              >
                Use Latest
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SearchIcon />}
                disabled={isWorking}
                sx={{ flex: 1, whiteSpace: 'nowrap' }}
              >
                Fresh Scrape
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      <Divider sx={{ my: 3 }} />

      {currentAnalysis && availableNights.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            py: 2,
            px: 3,
            bgcolor: 'grey.100',
            borderRadius: '12px',
            mb: 3,
            alignItems: 'center',
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary', mr: 1 }}>
            Number of Nights:
          </Typography>
          {availableNights.map((n) => (
            <Box
              key={n}
              component="span"
              onClick={() => setSelectedNights(n)}
              sx={{
                px: 2.5,
                py: 1.25,
                borderRadius: '8px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                border: 2,
                borderColor: selectedNights === n ? 'primary.main' : 'grey.400',
                bgcolor: selectedNights === n ? 'primary.main' : 'white',
                color: selectedNights === n ? 'white' : 'text.primary',
                userSelect: 'none',
                transition: 'all 0.2s',
              }}
            >
              {n} {n === 1 ? 'Night' : 'Nights'}
            </Box>
          ))}
        </Box>
      )}

      {isWorking && (
        <Box sx={{ mt: 3 }}>
          <LinearProgress variant="determinate" value={progress} sx={{ mb: 1 }} />
          <Typography variant="body2" color="textSecondary">
            {progressText}
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {!currentAnalysis && !isWorking && !error && <RecentScrapes onSelectScrape={onSelectRecentScrape} />}

      {currentAnalysis && (
        <>
          <Box sx={{ mt: 4 }} ref={reportRef}>
            <ParityReport
              analysis={currentAnalysis}
              revenueHooks={revenueHooks}
              rateStrategy={{
                ratePlans,
                ratePlansLoading,
                ratePlansError,
                lpcPromotionalDiscounts,
                promotionsLoading,
                promotionsError,
              }}
              offerBk={offerId}
              onGenerateEmail={isHealthy ? undefined : handleGenerateEmail}
              onExportPdf={handleExportPdf}
            />
          </Box>
          {!isHealthy && (
            <Box ref={emailRef}>
              <EmailGenerator
                analysis={currentAnalysis}
                revenueHooks={revenueHooks}
                ratePlanName={cheapestPlan?.name ?? ''}
                ratePlanCode={cheapestPlan?.rate_plan_code ?? ''}
                expanded={emailExpanded}
                onExpandedChange={setEmailExpanded}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
